<template>
  <div class="guide-signer-payments">
    <h2>What are signer payments?</h2>
    <p>
      Signer payments let you collect a fee from one or more signers as part of the
      signing ceremony. A service provider can charge a fee, a landlord can collect
      a deposit alongside a lease, or a vendor can require payment before a contract
      takes effect. The payment is tied to the package - signers validate a payment
      method during the ceremony, and funds are collected automatically once every
      signer has finished signing.
    </p>
    <p>
      Payments are processed through our payment processing partner,
      <strong>Stripe</strong>, one of the world's largest and most trusted payment
      platforms. Signers can pay with credit cards, debit cards, and other payment
      methods supported by Stripe. Funds are deposited directly into your linked
      bank account, minus a small platform processing fee.
    </p>

    <q-separator class="q-my-lg" />

    <h2>How the payment flow works</h2>
    <p>
      The payment flow is designed so that signers are never charged until every
      participant has completed their part of the signing ceremony. This protects
      both the sender and the signer - no one pays for a transaction that doesn't
      reach completion.
    </p>

    <h3>Step 1: Payment method pre-authorized during signing</h3>
    <p>
      When a signer marked as a payer opens the signing ceremony, they are prompted
      to enter their payment details via a secure, Stripe-hosted form before they
      can begin completing their fields. The payment method is validated and
      pre-authorized but <strong>not charged</strong> at this point. The signer
      sees the exact amount and a description of what they are paying for, and must
      consent before proceeding.
    </p>

    <h3>Step 2: Authorization hold after all signers complete</h3>
    <p>
      Once every signer in the package has completed their fields, the platform
      places an <strong>authorization hold</strong> on each payer's payment method
      for their designated amount. An auth hold reserves the funds without
      actually moving money - similar to how a hotel hold works. The signer may
      see a "pending" charge on their statement during this phase.
    </p>

    <h3>Step 3: Capture (actual charge)</h3>
    <p>
      Immediately after all authorization holds succeed, the platform
      <strong>captures</strong> (finalises) each hold. This is when funds are
      actually transferred. The package then transitions to
      <code>Completed</code> status and signed documents become available for
      download.
    </p>

    <q-banner class="bg-blue-1 text-blue-9 q-my-md" rounded>
      <strong>Key point:</strong> Signers pre-authorize their payment method during
      signing, but no money moves until every signer has finished. If a multi-signer
      package has three signers and only two have completed, no one is charged yet.
    </q-banner>

    <q-separator class="q-my-lg" />

    <h2>What signers see</h2>
    <p>
      Transparency is built into the payment experience. Here is what a signer
      encounters at each stage:
    </p>
    <ul>
      <li>
        <strong>During the ceremony:</strong> A secure payment form appears before
        the signer's fields. The form shows the amount, a description (for example
        "Document Processing Fee - $25.00"), and a consent statement. The signer
        enters their payment details and agrees to the charge. After pre-authorizing,
        they proceed to sign normally.
      </li>
      <li>
        <strong>After pre-authorization:</strong> The signer completes their fields
        just like any other signer. There is no further payment interaction during
        signing.
      </li>
      <li>
        <strong>Pending charge:</strong> Once all signers complete and the auth
        hold is placed, the signer may see a pending charge on their bank or
        payment statement. This resolves to a final charge within moments once
        capture completes.
      </li>
    </ul>

    <q-separator class="q-my-lg" />

    <h2>What happens when a payment fails</h2>
    <p>
      If a payer's payment method is declined during the authorization phase - for
      example, due to insufficient funds or an expired card - the platform does not
      charge any of the other payers. Instead:
    </p>
    <ol>
      <li>
        The package enters <code>PaymentFailed</code> status. Signed documents are
        not released yet.
      </li>
      <li>
        The affected signer receives a <strong>recovery email</strong> with a
        secure, time-limited link (default: 72 hours) to update their payment method.
      </li>
      <li>
        Once the signer provides a valid payment method via the recovery link, the
        platform automatically re-attempts the authorization and capture batch for
        all payers.
      </li>
    </ol>
    <p>
      If capture fails for one payer after other captures have already succeeded,
      the platform <strong>automatically refunds</strong> all captured payments.
      All payers receive recovery emails so the batch can be retried cleanly. This
      all-or-nothing model ensures no payer is charged for an incomplete transaction.
    </p>

    <q-banner class="bg-amber-1 text-amber-10 q-my-md" rounded>
      <strong>Auth hold expiration:</strong> Authorization holds expire after
      approximately 5–7 days (depending on the payment network). If the recovery
      window passes without the failed payer updating their payment method, the
      existing holds expire naturally and funds are released back to the other
      payers.
    </q-banner>

    <q-separator class="q-my-lg" />

    <h2>Per-signer payment amounts</h2>
    <p>
      Each signer can have a different payment amount. For example, in a real estate
      transaction the buyer might pay a $500 earnest deposit while the seller pays
      a $50 processing fee. Set <code>paymentAmount</code> and
      <code>paymentDescription</code> individually on each payer signer.
    </p>
    <p>
      Not every signer needs to be a payer. A package can have five signers but only
      collect payment from two of them - the others sign without any payment prompt.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Refunds</h2>
    <p>
      After a payment has been captured, you can issue full or partial refunds from
      the <strong>Payments</strong> page in the portal. Refunds are processed back
      to the original payment method and typically appear on the signer's statement
      within 5–10 business days.
    </p>
    <p>
      If the package is voided while payments are in the authorization phase (holds
      placed but not yet captured), the holds are cancelled automatically and funds
      are released without any refund needed.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Chargebacks, disputes, and your responsibilities</h2>
    <p>
      When you collect payments through the platform, <strong>you are the merchant
      of record</strong>. This means:
    </p>
    <ul>
      <li>
        <strong>Customer service.</strong> Signers who have questions about a charge
        will contact you, not the platform. You are responsible for responding to
        payment inquiries, explaining charges, and resolving complaints.
      </li>
      <li>
        <strong>Refund decisions.</strong> You decide when and whether to issue
        refunds. The platform provides the tools to process refunds from the
        <strong>Payments</strong> page, but the decision and any associated
        communication with the signer are your responsibility.
      </li>
      <li>
        <strong>Chargebacks and disputes.</strong> If a signer disputes a charge
        with their bank, the chargeback is filed against your account. You are
        responsible for responding to disputes and providing evidence (the platform
        retains signing audit trails and payment consent records that can support
        your case). Unresolved disputes may result in the funds being returned to
        the signer and a dispute fee charged to your account by the payment
        processor.
      </li>
      <li>
        <strong>Compliance.</strong> You are responsible for ensuring that the fees
        you charge comply with applicable laws and regulations in your jurisdiction,
        including any disclosure requirements, maximum fee limits, or consumer
        protection rules.
      </li>
    </ul>

    <q-banner class="bg-amber-1 text-amber-10 q-my-md" rounded>
      <strong>Tip:</strong> Clear payment descriptions and transparent communication
      with your signers are the best defence against chargebacks. Use the
      <code>paymentDescription</code> field to set a label that will be recognisable
      on the signer's bank statement.
    </q-banner>

    <q-separator class="q-my-lg" />

    <h2>Platform fees</h2>
    <p>
      Each payment has a small platform processing fee deducted before funds are
      deposited to your bank account. The fee is calculated as a percentage of
      the payment amount with a minimum floor, both determined by your subscription
      plan. You can see the gross amount, the fee, and the net deposit for every
      payment on the <strong>Payments</strong> page in the portal.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Prerequisites</h2>
    <p>
      Before you can collect signer payments, two things must be in place:
    </p>
    <ol>
      <li>
        <strong>Payment account setup.</strong> You need to link a bank account
        where payment deposits will be sent. Set this up from
        <strong>Settings &gt; Payments</strong> in the portal. You will be guided
        through identity verification and bank account setup by our payment
        processing partner, Stripe. Once your account is verified and payments
        are enabled, you can start collecting.
      </li>
      <li>
        <strong>Payment Collection Addendum.</strong> You must accept the Payment
        Collection Addendum in the portal. This is a legal agreement that governs
        how payment collection works on the platform. The addendum is versioned -
        if it is updated, you will be prompted to re-accept before sending new
        packages with payments.
      </li>
    </ol>

    <q-separator class="q-my-lg" />

    <h2>Payments on templates</h2>
    <p>
      Template roles support a default payment amount and description. When you
      create a role such as "Buyer" on a template and set a
      <code>paymentAmount</code>, every package created from that template will
      inherit the payment configuration for the mapped signer. This is useful for
      standardising fees - for example, always charging a $25 processing fee on a
      specific document type.
    </p>

    <q-separator class="q-my-lg" />

    <h2>Setting up payments in the portal</h2>
    <p>
      In the package builder, open the <strong>Signers</strong> panel and expand a
      signer's settings. Toggle <strong>Collect payment</strong> on, then enter the
      amount and an optional description. The description is shown to the signer
      during the ceremony, so use a clear label like "Document Processing Fee" or
      "Application Fee".
    </p>
    <p>
      For templates, open the template editor, select a role, and configure the
      payment amount in the role settings panel.
    </p>

    <section class="dev-section">
    <h2>For developers and enterprise integration</h2>
    <p>
      Payment configuration is set when creating or updating signers via the API.
      The payment lifecycle (pre-authorize → hold → capture) is fully managed by
      the platform - your integration only needs to configure the amount and listen
      for webhook events.
    </p>

    <h3>Configuring payment on a signer</h3>
    <p>
      Set <code>paymentAmount</code> (decimal, required for payers) and
      <code>paymentDescription</code> (string, optional but recommended) when
      adding a signer. Any signer with a non-zero <code>paymentAmount</code>
      becomes a payer.
    </p>
    <GuideCodeTabs :samples="samples.addPayer" />

    <h3>Multiple payers with different amounts</h3>
    <p>
      Use the bulk signer endpoint to add multiple payers with different amounts
      in a single request:
    </p>
    <GuideCodeTabs :samples="samples.bulkPayers" />

    <h3>Template role payment defaults</h3>
    <p>
      Set payment defaults on template roles so every package created from the
      template inherits the fee configuration:
    </p>
    <GuideCodeTabs :samples="samples.templatePayment" />

    <q-separator class="q-my-lg" />

    <h3>Payment lifecycle</h3>
    <p>
      The platform manages the full payment lifecycle automatically. Your
      integration does not need to initiate auth holds or captures - these happen
      server-side when all signers complete. The lifecycle is:
    </p>
    <ol>
      <li>
        <strong>Pending</strong> - Payment record created when the package is sent
        and the signer has a payment amount configured.
      </li>
      <li>
        <strong>MethodSaved</strong> - Signer pre-authorized their payment method
        during the ceremony. No charge yet.
      </li>
      <li>
        <strong>Authorized</strong> - All signers completed. Auth hold placed.
        Funds are reserved on the payment method.
      </li>
      <li>
        <strong>Succeeded</strong> - Auth hold captured. Funds transferred to your
        bank account (minus platform fee).
      </li>
    </ol>
    <p>
      Failure states: <code>AuthFailed</code> (payment method declined at auth),
      <code>CaptureFailed</code> (capture failed after auth), and
      <code>RecoveryPending</code> (recovery email sent, awaiting signer action).
    </p>

    <q-separator class="q-my-lg" />

    <h3>Webhook events</h3>
    <p>
      Subscribe to these webhook events to track payment progress in your system.
      See the <a href="/Docs/IntegrationGuides?topic=webhooks">Webhooks</a> guide
      for setup instructions.
    </p>

    <div class="q-my-md" style="overflow-x: auto;">
      <table class="q-table" style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Event</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Fires when</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>payment.method_saved</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">A payer pre-authorized their payment method during the signing ceremony</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>payment.authorized</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Auth hold placed on a payer's payment method (funds reserved, not charged)</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>payment.succeeded</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Capture completed - funds transferred to your account</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>payment.failed</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Auth or capture was declined</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>payment.recovery_initiated</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Recovery email sent after a payment failure; includes the recovery link expiration</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>payment.batch_completed</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">All payers in the package processed; includes success/failure counts</td>
          </tr>
        </tbody>
      </table>
    </div>

    <q-separator class="q-my-lg" />

    <h3>Updating payment on a draft signer</h3>
    <p>
      While the package is in <code>Draft</code>, use
      <code>PUT /api/v1/packages/{id}/signers/{signerId}</code> to change the
      payment amount or description. Partial-update semantics apply.
    </p>
    <GuideCodeTabs :samples="samples.updatePayment" />

    <q-separator class="q-my-lg" />

    <h3>Payment fields reference</h3>
    <div class="q-my-md" style="overflow-x: auto;">
      <table class="q-table" style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Field</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Type</th>
            <th style="text-align: left; padding: 8px; border-bottom: 2px solid #e0e0e0;">Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>paymentAmount</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">decimal</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Amount to charge the signer, in the tenant's currency. Setting a non-zero value makes this signer a payer.</td>
          </tr>
          <tr>
            <td style="padding: 8px; border-bottom: 1px solid #eee;"><code>paymentDescription</code></td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">string</td>
            <td style="padding: 8px; border-bottom: 1px solid #eee;">Displayed to the signer during the payment consent step. Use a clear label (e.g. "Document Processing Fee").</td>
          </tr>
        </tbody>
      </table>
    </div>

    <q-separator class="q-my-lg" />

    <h3>MCP tool equivalents</h3>
    <p>
      The same payment fields are available on the MCP signer tools:
    </p>
    <ul>
      <li><code>add_signer</code> - set <code>paymentAmount</code> and <code>paymentDescription</code></li>
      <li><code>add_signers_bulk</code> - each signer object supports payment fields</li>
      <li><code>update_signer</code> - change payment amount on a draft signer</li>
      <li><code>add_template_role</code> - set <code>paymentAmount</code> and <code>paymentDescription</code> as role defaults</li>
    </ul>

    <q-separator class="q-my-lg" />

    <h3>Best practices</h3>
    <ul>
      <li>
        <strong>Always set a clear description.</strong> Signers are more likely to
        complete payment when they understand what they are being charged for. Use
        descriptive labels like "Document Processing Fee" rather than generic terms.
      </li>
      <li>
        <strong>Use templates for recurring fee structures.</strong> If you
        consistently charge the same fee for a document type, configure the payment
        amount on the template role. This prevents manual entry errors and ensures
        consistent pricing.
      </li>
      <li>
        <strong>Listen for <code>payment.failed</code> and <code>payment.recovery_initiated</code> webhooks.</strong>
        These events let your system proactively notify your internal team when a
        signer needs to update their payment method, rather than waiting for the
        recovery window to expire.
      </li>
      <li>
        <strong>Account for the auth-then-capture delay.</strong> Signers may see a
        pending charge on their statement between the auth hold and capture. If
        your support team receives inquiries about pending charges, this is normal
        and resolves within minutes once all holds are captured.
      </li>
      <li>
        <strong>Plan for multi-payer packages.</strong> When multiple signers are
        payers, the platform batches all authorizations and captures together. If
        any single payer's payment method is declined, no one is charged. Build your workflow
        to handle the <code>PaymentFailed</code> state gracefully.
      </li>
    </ul>

    <q-banner class="bg-green-1 text-green-9 q-mt-lg" rounded>
      <template v-slot:avatar>
        <q-icon name="menu_book" color="green-9" />
      </template>
      See the <strong>Signers</strong> and <strong>Packages</strong> sections in the
      <a href="/Docs/ApiReference" class="text-green-9" style="text-decoration: underline;">API Reference</a>
      for complete endpoint schemas and try-it-out examples.
    </q-banner>
    </section>
  </div>
</template>

<script>
import GuideCodeTabs from './shared/GuideCodeTabs.vue';

const API = 'https://api.esignlaunchpad.com';

const samples = {
    // ─── Add a single payer signer ─────────────────────────────────────────────
    addPayer: {
        curl: `curl -X POST ${API}/api/v1/packages/$PACKAGE_ID/signers \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{
    "name": "Jane Doe",
    "email": "jane@example.com",
    "paymentAmount": 25.00,
    "paymentDescription": "Document Processing Fee"
  }'`,

        js: `await fetch(\`${API}/api/v1/packages/\${packageId}/signers\`, {
  method: 'POST',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({
    name: 'Jane Doe',
    email: 'jane@example.com',
    paymentAmount: 25.00,
    paymentDescription: 'Document Processing Fee'
  })
});`,

        node: `await fetch(\`${API}/api/v1/packages/\${packageId}/signers\`, {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    name: 'Jane Doe',
    email: 'jane@example.com',
    paymentAmount: 25.00,
    paymentDescription: 'Document Processing Fee'
  })
});`,

        python: `requests.post(
    f'${API}/api/v1/packages/{package_id}/signers',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={
        'name': 'Jane Doe',
        'email': 'jane@example.com',
        'paymentAmount': 25.00,
        'paymentDescription': 'Document Processing Fee'
    }
).raise_for_status()`,

        csharp: `await client.PostAsJsonAsync(
    $"api/v1/packages/{packageId}/signers",
    new
    {
        name = "Jane Doe",
        email = "jane@example.com",
        paymentAmount = 25.00m,
        paymentDescription = "Document Processing Fee"
    });`,

        php: `$client->post("api/v1/packages/{$packageId}/signers", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => [
        'name' => 'Jane Doe',
        'email' => 'jane@example.com',
        'paymentAmount' => 25.00,
        'paymentDescription' => 'Document Processing Fee',
    ],
]);`,

        ruby: `uri = URI("${API}/api/v1/packages/#{package_id}/signers")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = {
  name: 'Jane Doe',
  email: 'jane@example.com',
  paymentAmount: 25.00,
  paymentDescription: 'Document Processing Fee'
}.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }`
    },

    // ─── Bulk add payers with different amounts ────────────────────────────────
    bulkPayers: {
        curl: `curl -X POST ${API}/api/v1/packages/$PACKAGE_ID/signers/bulk \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{
    "signers": [
      {
        "name": "Alex Chen",
        "email": "alex@acme.com",
        "paymentAmount": 500.00,
        "paymentDescription": "Earnest Deposit"
      },
      {
        "name": "Pat Rivera",
        "email": "pat@acme.com",
        "paymentAmount": 50.00,
        "paymentDescription": "Processing Fee"
      },
      {
        "name": "Legal Dept",
        "email": "legal@acme.com",
        "type": "CarbonCopy"
      }
    ]
  }'`,

        js: `await fetch(\`${API}/api/v1/packages/\${packageId}/signers/bulk\`, {
  method: 'POST',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({
    signers: [
      { name: 'Alex Chen',   email: 'alex@acme.com',  paymentAmount: 500.00, paymentDescription: 'Earnest Deposit' },
      { name: 'Pat Rivera',  email: 'pat@acme.com',   paymentAmount: 50.00,  paymentDescription: 'Processing Fee' },
      { name: 'Legal Dept',  email: 'legal@acme.com', type: 'CarbonCopy' }
    ]
  })
});`,

        node: `await fetch(\`${API}/api/v1/packages/\${packageId}/signers/bulk\`, {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    signers: [
      { name: 'Alex Chen',   email: 'alex@acme.com',  paymentAmount: 500.00, paymentDescription: 'Earnest Deposit' },
      { name: 'Pat Rivera',  email: 'pat@acme.com',   paymentAmount: 50.00,  paymentDescription: 'Processing Fee' },
      { name: 'Legal Dept',  email: 'legal@acme.com', type: 'CarbonCopy' }
    ]
  })
});`,

        python: `requests.post(
    f'${API}/api/v1/packages/{package_id}/signers/bulk',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={
        'signers': [
            {'name': 'Alex Chen',   'email': 'alex@acme.com',  'paymentAmount': 500.00, 'paymentDescription': 'Earnest Deposit'},
            {'name': 'Pat Rivera',  'email': 'pat@acme.com',   'paymentAmount': 50.00,  'paymentDescription': 'Processing Fee'},
            {'name': 'Legal Dept',  'email': 'legal@acme.com', 'type': 'CarbonCopy'},
        ]
    }
).raise_for_status()`,

        csharp: `var signers = new object[]
{
    new { name = "Alex Chen",   email = "alex@acme.com",  paymentAmount = 500.00m, paymentDescription = "Earnest Deposit" },
    new { name = "Pat Rivera",  email = "pat@acme.com",   paymentAmount = 50.00m,  paymentDescription = "Processing Fee" },
    new { name = "Legal Dept",  email = "legal@acme.com", type = "CarbonCopy" },
};

await client.PostAsJsonAsync(
    $"api/v1/packages/{packageId}/signers/bulk",
    new { signers });`,

        php: `$signers = [
    ['name' => 'Alex Chen',   'email' => 'alex@acme.com',  'paymentAmount' => 500.00, 'paymentDescription' => 'Earnest Deposit'],
    ['name' => 'Pat Rivera',  'email' => 'pat@acme.com',   'paymentAmount' => 50.00,  'paymentDescription' => 'Processing Fee'],
    ['name' => 'Legal Dept',  'email' => 'legal@acme.com', 'type' => 'CarbonCopy'],
];

$client->post("api/v1/packages/{$packageId}/signers/bulk", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => ['signers' => $signers],
]);`,

        ruby: `signers = [
  { name: 'Alex Chen',   email: 'alex@acme.com',  paymentAmount: 500.00, paymentDescription: 'Earnest Deposit' },
  { name: 'Pat Rivera',  email: 'pat@acme.com',   paymentAmount: 50.00,  paymentDescription: 'Processing Fee' },
  { name: 'Legal Dept',  email: 'legal@acme.com', type: 'CarbonCopy' }
]

uri = URI("${API}/api/v1/packages/#{package_id}/signers/bulk")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = { signers: signers }.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }`
    },

    // ─── Template role with payment defaults ──────────────────────────────────
    templatePayment: {
        curl: `curl -X POST ${API}/api/v1/templates/$TEMPLATE_ID/roles \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{
    "name": "Client",
    "paymentAmount": 25.00,
    "paymentDescription": "Document Processing Fee",
    "routingOrder": 1
  }'`,

        js: `await fetch(\`${API}/api/v1/templates/\${templateId}/roles\`, {
  method: 'POST',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({
    name: 'Client',
    paymentAmount: 25.00,
    paymentDescription: 'Document Processing Fee',
    routingOrder: 1
  })
});`,

        node: `await fetch(\`${API}/api/v1/templates/\${templateId}/roles\`, {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    name: 'Client',
    paymentAmount: 25.00,
    paymentDescription: 'Document Processing Fee',
    routingOrder: 1
  })
});`,

        python: `requests.post(
    f'${API}/api/v1/templates/{template_id}/roles',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={
        'name': 'Client',
        'paymentAmount': 25.00,
        'paymentDescription': 'Document Processing Fee',
        'routingOrder': 1
    }
).raise_for_status()`,

        csharp: `await client.PostAsJsonAsync(
    $"api/v1/templates/{templateId}/roles",
    new
    {
        name = "Client",
        paymentAmount = 25.00m,
        paymentDescription = "Document Processing Fee",
        routingOrder = 1
    });`,

        php: `$client->post("api/v1/templates/{$templateId}/roles", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => [
        'name' => 'Client',
        'paymentAmount' => 25.00,
        'paymentDescription' => 'Document Processing Fee',
        'routingOrder' => 1,
    ],
]);`,

        ruby: `uri = URI("${API}/api/v1/templates/#{template_id}/roles")
req = Net::HTTP::Post.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = {
  name: 'Client',
  paymentAmount: 25.00,
  paymentDescription: 'Document Processing Fee',
  routingOrder: 1
}.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }`
    },

    // ─── Update payment amount on a draft signer ──────────────────────────────
    updatePayment: {
        curl: `curl -X PUT ${API}/api/v1/packages/$PACKAGE_ID/signers/$SIGNER_ID \\
  -H "Authorization: Bearer $ESIGN_TOKEN" -H "Content-Type: application/json" \\
  -d '{
    "paymentAmount": 35.00,
    "paymentDescription": "Updated Service Fee"
  }'`,

        js: `await fetch(\`${API}/api/v1/packages/\${packageId}/signers/\${signerId}\`, {
  method: 'PUT',
  headers: { 'Authorization': 'Bearer ' + ESIGN_TOKEN, 'Content-Type': 'application/json' },
  body: JSON.stringify({
    paymentAmount: 35.00,
    paymentDescription: 'Updated Service Fee'
  })
});`,

        node: `await fetch(\`${API}/api/v1/packages/\${packageId}/signers/\${signerId}\`, {
  method: 'PUT',
  headers: {
    'Authorization': 'Bearer ' + process.env.ESIGN_TOKEN,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    paymentAmount: 35.00,
    paymentDescription: 'Updated Service Fee'
  })
});`,

        python: `requests.put(
    f'${API}/api/v1/packages/{package_id}/signers/{signer_id}',
    headers={'Authorization': f"Bearer {os.environ['ESIGN_TOKEN']}"},
    json={
        'paymentAmount': 35.00,
        'paymentDescription': 'Updated Service Fee'
    }
).raise_for_status()`,

        csharp: `await client.PutAsJsonAsync(
    $"api/v1/packages/{packageId}/signers/{signerId}",
    new
    {
        paymentAmount = 35.00m,
        paymentDescription = "Updated Service Fee"
    });`,

        php: `$client->put("api/v1/packages/{$packageId}/signers/{$signerId}", [
    'headers' => ['Authorization' => 'Bearer ' . getenv('ESIGN_TOKEN')],
    'json' => [
        'paymentAmount' => 35.00,
        'paymentDescription' => 'Updated Service Fee',
    ],
]);`,

        ruby: `uri = URI("${API}/api/v1/packages/#{package_id}/signers/#{signer_id}")
req = Net::HTTP::Put.new(uri)
req['Authorization'] = "Bearer #{ENV.fetch('ESIGN_TOKEN')}"
req['Content-Type'] = 'application/json'
req.body = {
  paymentAmount: 35.00,
  paymentDescription: 'Updated Service Fee'
}.to_json
Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |h| h.request(req) }`
    }
};

export default {
    name: 'GuideSignerPayments',
    components: { GuideCodeTabs },
    data() { return { samples }; }
};
</script>
